/**
 * CVPage.jsx — CV interactif immersif de Bruno Llorens
 *
 * Route publique : /cv
 *
 * Structure (scrollable, sections ancrées) :
 *   1. Hero + accroche + chiffres clés
 *   2. Timeline expériences (dépliables)
 *   3. Prestations grid (8 cards)
 *   4. Compétences par famille (chips filtrables)
 *   5. Projet notable (code2roues.fr)
 *   6. CTA contact + Télécharger PDF + Retour vers vcard
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Mail, MapPin, Globe, Clock, Download, UserPlus,
  Sparkles, CheckCircle2, ChevronRight, Briefcase, GraduationCap,
  Award, Rocket, Target, ArrowDown, ArrowLeft, ExternalLink, Quote, Star, Trophy,
} from 'lucide-react';
import { CV } from '../data/brunoCV';

const SKILL_FAMILIES = {
  all:    { label: 'Toutes', color: 'from-orange-500 to-amber-500' },
  auto:   { label: '🚗 Auto', color: 'from-blue-500 to-cyan-500' },
  moto:   { label: '🏍️ Moto', color: 'from-orange-500 to-red-500' },
  commun: { label: '🎯 Transverse', color: 'from-emerald-500 to-teal-500' },
};

export default function CVPage() {
  const [skillFilter, setSkillFilter] = useState('all');
  const [expandedXP, setExpandedXP] = useState(0); // Première expérience ouverte par défaut

  const filteredSkills = skillFilter === 'all'
    ? CV.competences.techniques
    : CV.competences.techniques.filter((s) => s.famille === skillFilter);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white relative overflow-hidden"
      data-testid="cv-page"
    >
      {/* Halos décoratifs globaux */}
      <div aria-hidden className="absolute top-0 -left-32 w-[500px] h-[500px] bg-orange-600/20 rounded-full blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute top-1/4 -right-40 w-[400px] h-[400px] bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute bottom-20 left-1/4 w-[380px] h-[380px] bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* ═══════════ NAV TOP ═══════════ */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/70 border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-3">
          <Link
            to="/vcard"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-300 hover:text-orange-300 transition"
            data-testid="cv-back-vcard"
          >
            <ArrowLeft className="w-4 h-4" />
            Ma carte
          </Link>
          <div className="text-[10px] tracking-widest uppercase text-orange-300 font-black">
            CV Interactif
          </div>
          <a
            href={CV.cv_pdf}
            download="bruno-llorens-cv-2027.pdf"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-300 hover:text-orange-300 transition"
            data-testid="cv-dl-pdf-top"
          >
            <Download className="w-4 h-4" />
            PDF
          </a>
        </div>
      </div>

      <div className="relative max-w-4xl mx-auto px-4 py-10 md:py-16">

        {/* ═══════════ HERO ═══════════ */}
        <section className="text-center mb-14" data-testid="cv-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 mb-5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-200">
              {CV.contact.dispo}
            </span>
          </div>
          <h1
            className="text-4xl md:text-6xl font-black text-white leading-tight tracking-tight"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            {CV.nom}
          </h1>
          <p className="text-base md:text-lg text-orange-300 font-bold mt-3">
            {CV.titre}
          </p>
          <p className="text-[11px] md:text-xs text-slate-400 mt-1 tracking-wider uppercase">
            {CV.sousTitre}
          </p>

          {/* Accroche en citation */}
          <blockquote className="relative mt-6 md:mt-8 max-w-2xl mx-auto">
            <div className="absolute -top-2 -left-2 text-6xl text-orange-500/30 font-black select-none" style={{ fontFamily: 'Fraunces, serif' }}>
              "
            </div>
            <p className="relative text-base md:text-lg text-slate-200 italic px-6 md:px-8 py-2 leading-relaxed">
              {CV.accroche}
            </p>
            <div className="absolute -bottom-6 -right-2 text-6xl text-orange-500/30 font-black select-none" style={{ fontFamily: 'Fraunces, serif' }}>
              "
            </div>
          </blockquote>

          {/* Chiffres clés */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10 max-w-3xl mx-auto">
            {CV.chiffres.map((c, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-white/[0.05] to-white/[0.01] border border-white/10 rounded-2xl p-4 text-center hover:border-orange-400/40 transition-colors"
                data-testid={`cv-chiffre-${i}`}
              >
                <div
                  className="text-3xl md:text-4xl font-black text-orange-400"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  {c.valeur}
                </div>
                <div className="text-[11px] font-bold text-white mt-1 uppercase tracking-wider">
                  {c.label}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">{c.sub}</div>
              </div>
            ))}
          </div>

          {/* Scroll hint */}
          <div className="mt-10 flex justify-center">
            <div className="inline-flex items-center gap-2 text-[11px] text-slate-400 uppercase tracking-widest animate-bounce">
              <ArrowDown className="w-3.5 h-3.5" />
              Découvrir le parcours
            </div>
          </div>
        </section>

        {/* ═══════════ POINTS CLÉS ═══════════ */}
        <section className="mb-14" data-testid="cv-cles">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {CV.cles.map((k, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-400/20 rounded-2xl p-4 hover:border-orange-400/50 transition"
              >
                <div className="text-3xl mb-2">{k.icon}</div>
                <div className="text-[10px] uppercase tracking-widest text-orange-300 font-black">
                  {k.label}
                </div>
                <div className="text-sm font-bold text-white mt-1">{k.value}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════ TIMELINE EXPÉRIENCES ═══════════ */}
        <section className="mb-14" data-testid="cv-experiences">
          <SectionHeader icon={Briefcase} title="Parcours professionnel" subtitle="Expériences & diplômes" />

          <div className="relative mt-8">
            {/* Ligne verticale timeline */}
            <div className="absolute left-5 md:left-6 top-0 bottom-0 w-px bg-gradient-to-b from-orange-400/60 via-orange-500/40 to-transparent" aria-hidden />

            <div className="space-y-5">
              {CV.experiences.map((xp, i) => {
                const isOpen = expandedXP === i;
                return (
                  <div key={i} className="relative pl-14 md:pl-16" data-testid={`cv-xp-${i}`}>
                    {/* Point timeline */}
                    <div
                      className={`absolute left-0 top-2 w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center ${
                        xp.current
                          ? 'bg-gradient-to-br from-orange-500 to-orange-600 border-orange-400 shadow-[0_0_20px_rgba(249,115,22,0.4)]'
                          : 'bg-slate-800 border-orange-400/40'
                      }`}
                    >
                      {xp.current ? (
                        <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-white" />
                      ) : (
                        <span className="text-[10px] font-black text-orange-300">
                          {CV.experiences.length - i}
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => setExpandedXP(isOpen ? -1 : i)}
                      className={`w-full text-left bg-gradient-to-br from-slate-800/80 to-slate-900/80 border rounded-2xl p-4 md:p-5 transition-all ${
                        isOpen
                          ? 'border-orange-400/50 shadow-[0_10px_30px_-10px_rgba(249,115,22,0.4)]'
                          : 'border-white/10 hover:border-orange-400/30'
                      }`}
                      data-testid={`cv-xp-btn-${i}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base md:text-lg font-bold text-white">
                              {xp.poste}
                            </h3>
                            {xp.current && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-[9px] font-black uppercase tracking-widest text-emerald-200">
                                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                                Actuel
                              </span>
                            )}
                          </div>
                          <div className="text-[13px] text-orange-300 font-semibold mt-0.5">
                            {xp.entreprise}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {xp.periode}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {xp.lieu}
                            </span>
                          </div>
                        </div>
                        <ChevronRight
                          className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${
                            isOpen ? 'rotate-90' : ''
                          }`}
                        />
                      </div>

                      {/* Détails dépliables */}
                      <div
                        className={`grid transition-all duration-500 ease-out ${
                          isOpen ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0 mt-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="text-[13px] text-slate-300 leading-relaxed">
                            {xp.description}
                          </p>
                          {xp.realisations?.length > 0 && (
                            <ul className="mt-3 space-y-1.5">
                              {xp.realisations.map((r, j) => (
                                <li key={j} className="flex items-start gap-2 text-[12.5px] text-slate-300">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 shrink-0 mt-0.5" />
                                  <span>{r}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {xp.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-3">
                              {xp.tags.map((t) => (
                                <span
                                  key={t}
                                  className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide bg-orange-500/10 border border-orange-400/30 text-orange-200"
                                >
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════ PRESTATIONS (grid) ═══════════ */}
        <section className="mb-14" data-testid="cv-prestations">
          <SectionHeader icon={Rocket} title="Mes prestations" subtitle="Ce que je peux faire pour vous" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            {CV.prestations.map((p, i) => (
              <div
                key={i}
                className="group bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 rounded-2xl p-4 hover:border-orange-400/40 hover:bg-orange-500/[0.03] transition-all"
                data-testid={`cv-prestation-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="text-3xl shrink-0 group-hover:scale-110 transition-transform">
                    {p.icon}
                  </div>
                  <div>
                    <div className="font-bold text-white text-sm">{p.nom}</div>
                    <div className="text-[11.5px] text-slate-400 mt-1 leading-relaxed">
                      {p.detail}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════ COMPÉTENCES (filtrables) ═══════════ */}
        <section className="mb-14" data-testid="cv-competences">
          <SectionHeader icon={Target} title="Compétences techniques" subtitle="Filtrables par domaine" />

          {/* Filtres */}
          <div className="flex flex-wrap gap-2 mt-6 mb-5">
            {Object.entries(SKILL_FAMILIES).map(([key, { label, color }]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSkillFilter(key)}
                className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-all ${
                  skillFilter === key
                    ? `bg-gradient-to-r ${color} text-white shadow-lg scale-105`
                    : 'bg-white/[0.05] text-slate-300 border border-white/10 hover:bg-white/[0.1]'
                }`}
                data-testid={`cv-filter-${key}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {filteredSkills.map((s, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/15 text-[12px] font-semibold text-slate-100 hover:border-orange-400/40 transition"
              >
                {s.nom}
              </span>
            ))}
          </div>

          {/* Pédagogie + Soft skills */}
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <div className="bg-gradient-to-br from-blue-500/5 to-transparent border border-blue-400/20 rounded-2xl p-4">
              <h4 className="flex items-center gap-2 text-sm font-black text-blue-200 uppercase tracking-widest mb-3">
                <GraduationCap className="w-4 h-4" /> Pédagogie
              </h4>
              <ul className="space-y-1.5">
                {CV.competences.pedagogiques.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12.5px] text-slate-200">
                    <span className="text-blue-400 mt-1">▸</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-emerald-500/5 to-transparent border border-emerald-400/20 rounded-2xl p-4">
              <h4 className="flex items-center gap-2 text-sm font-black text-emerald-200 uppercase tracking-widest mb-3">
                <Sparkles className="w-4 h-4" /> Qualités humaines
              </h4>
              <ul className="space-y-1.5">
                {CV.competences.softSkills.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12.5px] text-slate-200">
                    <span className="text-emerald-400 mt-1">▸</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ═══════════ TÉMOIGNAGES / RÉSULTATS ÉLÈVES ═══════════ */}
        <section className="mb-14" data-testid="cv-temoignages">
          <SectionHeader icon={Star} title="Résultats élèves" subtitle="Témoignages & réussites" />

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            {CV.temoignages.map((t, i) => {
              const palettes = {
                amber: {
                  bg: 'from-amber-500/15 to-amber-600/5',
                  border: 'border-amber-400/40',
                  borderHover: 'hover:border-amber-400/70',
                  textLight: 'text-amber-300',
                  initial: 'from-amber-500 to-amber-600',
                  halo: 'bg-amber-500/20',
                },
                emerald: {
                  bg: 'from-emerald-500/15 to-emerald-600/5',
                  border: 'border-emerald-400/40',
                  borderHover: 'hover:border-emerald-400/70',
                  textLight: 'text-emerald-300',
                  initial: 'from-emerald-500 to-emerald-600',
                  halo: 'bg-emerald-500/20',
                },
                blue: {
                  bg: 'from-blue-500/15 to-blue-600/5',
                  border: 'border-blue-400/40',
                  borderHover: 'hover:border-blue-400/70',
                  textLight: 'text-blue-300',
                  initial: 'from-blue-500 to-blue-600',
                  halo: 'bg-blue-500/20',
                },
                orange: {
                  bg: 'from-orange-500/15 to-orange-600/5',
                  border: 'border-orange-400/40',
                  borderHover: 'hover:border-orange-400/70',
                  textLight: 'text-orange-300',
                  initial: 'from-orange-500 to-orange-600',
                  halo: 'bg-orange-500/20',
                },
              };
              const palette = palettes[t.couleur] || palettes.orange;

              return (
                <div
                  key={i}
                  className={`relative bg-gradient-to-br ${palette.bg} border ${palette.border} ${palette.borderHover} rounded-2xl p-5 transition-all overflow-hidden`}
                  data-testid={`cv-temoignage-${i}`}
                >
                  <div aria-hidden className={`absolute -top-16 -right-16 w-40 h-40 ${palette.halo} rounded-full blur-3xl pointer-events-none`} />

                  {/* Guillemet déco */}
                  <Quote className={`absolute top-4 right-4 w-6 h-6 ${palette.textLight} opacity-30`} />

                  <div className="relative flex items-center gap-3 mb-3">
                    {/* Avatar initiale */}
                    <div
                      className={`relative w-14 h-14 rounded-full bg-gradient-to-br ${palette.initial} flex items-center justify-center shadow-lg shrink-0 ring-2 ring-white/10`}
                    >
                      <span
                        className="text-xl font-black text-white"
                        style={{ fontFamily: 'Fraunces, serif' }}
                      >
                        {t.initiales}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div
                          className="text-lg font-bold text-white leading-tight"
                          style={{ fontFamily: 'Fraunces, serif' }}
                        >
                          {t.prenom}
                        </div>
                        {t.age && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[9.5px] font-bold tracking-wider text-slate-200">
                            {t.age}
                          </span>
                        )}
                      </div>
                      <div className={`text-[11px] font-bold uppercase tracking-widest ${palette.textLight} mt-0.5`}>
                        {t.formation}
                      </div>
                      {t.vehicule && (
                        <div className="text-[10.5px] text-slate-400 mt-0.5 italic">
                          {t.vehicule}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Chiffres clés : durée + résultat */}
                  <div className="relative grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-slate-900/40 border border-white/5 rounded-xl p-2.5 text-center">
                      <div
                        className={`text-2xl font-black ${palette.textLight}`}
                        style={{ fontFamily: 'Fraunces, serif' }}
                      >
                        {t.duree}
                      </div>
                      <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-black mt-0.5">
                        {t.dureeLabel || 'Préparation'}
                      </div>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-xl p-2.5 text-center flex flex-col items-center justify-center">
                      <Trophy className="w-4 h-4 text-emerald-300 mb-0.5" />
                      <div className="text-[10px] uppercase tracking-widest text-emerald-200 font-black leading-tight">
                        {t.resultat}
                      </div>
                    </div>
                  </div>

                  {/* Citation */}
                  <p className="relative text-[12.5px] text-slate-200 leading-relaxed italic">
                    « {t.citation.replace(/^« /, '').replace(/ »$/, '')} »
                  </p>
                </div>
              );
            })}
          </div>

          {/* Mention éthique en bas */}
          <p className="text-center text-[10px] text-slate-500 mt-5 italic">
            Témoignages anonymisés — prénoms et chiffres authentiques · prénoms réduits par respect de la vie privée
          </p>
        </section>

        {/* ═══════════ PROJETS ═══════════ */}
        <section className="mb-14" data-testid="cv-projets">
          <SectionHeader icon={Award} title="Projets notables" subtitle="Initiatives et créations" />
          <div className="mt-6 space-y-4">
            {CV.projets.map((p, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 border border-orange-400/30 rounded-2xl p-5 hover:border-orange-400/60 transition"
                data-testid={`cv-projet-${i}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold text-white">{p.nom}</h3>
                    <div className="text-[12px] text-orange-300 font-semibold mt-0.5">
                      {p.role}
                    </div>
                    <p className="text-[13px] text-slate-200 mt-3 leading-relaxed">
                      {p.description}
                    </p>
                    {p.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {p.tags.map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide bg-orange-500/15 border border-orange-400/30 text-orange-200"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {p.lien && (
                    <a
                      href={p.lien}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-orange-500/20 border border-orange-400/40 hover:bg-orange-500/30 transition shrink-0"
                      aria-label={`Ouvrir ${p.nom}`}
                    >
                      <ExternalLink className="w-4 h-4 text-orange-300" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════ CONTACT / CTA ═══════════ */}
        <section className="mb-10" data-testid="cv-contact">
          <div className="relative bg-gradient-to-br from-orange-500/20 via-amber-500/10 to-orange-500/5 border border-orange-400/40 rounded-3xl p-6 md:p-8 text-center overflow-hidden">
            <div aria-hidden className="absolute -top-20 -right-20 w-56 h-56 bg-orange-500/30 rounded-full blur-3xl" />
            <div aria-hidden className="absolute -bottom-20 -left-16 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl" />

            <div className="relative">
              <h2
                className="text-2xl md:text-3xl font-black text-white"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Discutons de votre besoin
              </h2>
              <p className="text-sm text-slate-200 mt-2 max-w-md mx-auto">
                Renfort, remplacement, prestation ponctuelle ou formation sur mesure — je suis à votre écoute 7j/7.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6 max-w-xl mx-auto">
                <a
                  href={CV.contact.telHref}
                  className="group inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-slate-950 font-black text-sm transition hover:scale-[1.03] active:scale-[0.97] shadow-lg"
                  data-testid="cv-cta-call"
                >
                  <Phone className="w-4 h-4" strokeWidth={2.5} />
                  Appeler
                </a>
                <a
                  href={CV.contact.emailHref}
                  className="group inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-white/20 bg-white/[0.08] hover:bg-white/[0.15] text-white font-bold text-sm transition hover:scale-[1.03] active:scale-[0.97]"
                  data-testid="cv-cta-email"
                >
                  <Mail className="w-4 h-4" strokeWidth={2.5} />
                  Email
                </a>
                <Link
                  to="/vcard"
                  className="group inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-white/20 bg-white/[0.08] hover:bg-white/[0.15] text-white font-bold text-sm transition hover:scale-[1.03] active:scale-[0.97]"
                  data-testid="cv-cta-vcard"
                >
                  <UserPlus className="w-4 h-4" strokeWidth={2.5} />
                  Ma carte
                </Link>
              </div>

              <a
                href={CV.cv_pdf}
                download="bruno-llorens-cv-2027.pdf"
                className="inline-flex items-center gap-2 mt-5 text-xs font-bold text-orange-200 hover:text-white transition tracking-wider"
                data-testid="cv-dl-pdf"
              >
                <Download className="w-3.5 h-3.5" />
                Télécharger ce CV en PDF (5 pages)
              </a>
            </div>
          </div>

          {/* Coordonnées pied */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-[12px] text-slate-400">
            <span className="inline-flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-orange-400" />
              {CV.contact.tel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-orange-400" />
              {CV.contact.email}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-orange-400" />
              {CV.contact.site}
            </span>
          </div>
          <p className="text-center text-[10px] text-slate-600 mt-4">
            {CV.contact.adresse}
          </p>
        </section>
      </div>
    </div>
  );
}

// ───────────────── Sous-composants ─────────────────
function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-500/25 to-amber-500/15 border border-orange-400/40 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-orange-300" />
      </div>
      <div>
        <h2
          className="text-xl md:text-2xl font-bold text-white leading-tight"
          style={{ fontFamily: 'Fraunces, serif' }}
        >
          {title}
        </h2>
        {subtitle && (
          <p className="text-[11px] text-slate-400 uppercase tracking-widest font-bold mt-1">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
